import { usePermissions } from 'commons/customHooks'
import i18n from 'commons/libs/i18n'
import { Tooltip } from 'components'
import { Icon } from 'components/Icon'
import { Link } from 'react-router-dom'
import { setOpenRecommendationModal } from 'store/StructuralContext/structuralSlice'
import { useDispatch } from 'store/store'

import { SIDEBAR_IDS } from '../constants'
import { allLinks, hasPermission } from '../helpers'
import { MenuItemContent } from '../MenuItemContent'
import { type MenuItem, useMenuActive } from '../MenuSidebar'
import * as S from '../styles'

type MenuItemNodeProps = {
  item: MenuItem
  collapsed?: boolean
  openItems: string[]
  onSubmenuToggle: (itemId: string, level: number) => void
  level: number
}

export const MenuItemNode = ({
  item,
  collapsed = false,
  openItems,
  onSubmenuToggle,
  level,
}: MenuItemNodeProps) => {
  const dispatch = useDispatch()
  const { verifyPermission } = usePermissions()
  const { isItemActive } = useMenuActive()

  const isOpen = openItems.includes(item.itemId)
  const links = item.menuItems ? allLinks(item.menuItems) : []
  const { active, selected } = isItemActive(item, links)

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()

    if (item.itemId === SIDEBAR_IDS.RECOMMENDATION) {
      dispatch(setOpenRecommendationModal(true))
    }

    if (item.menuItems) {
      onSubmenuToggle(item.itemId, level)
    }
  }

  const content = (
    <Tooltip arrow placement="right" title={collapsed && !isOpen ? i18n.t(item.titleKey) : ''}>
      <S.Flex alignItems="center" justifyContent="space-between">
        <MenuItemContent collapsed={collapsed} icon={item.leftIcon}>
          {i18n.t(item.titleKey)}
        </MenuItemContent>

        {!collapsed && item.menuItems && (
          <Icon icon={isOpen ? 'chevron-up' : 'chevron-down'} size="20" />
        )}
      </S.Flex>
    </Tooltip>
  )

  return (
    <S.ListItem
      active={active}
      collapsed={collapsed}
      data-testid={item.itemId}
      onClick={handleClick}
      selected={selected}
    >
      {item.to ? <Link to={item.to}>{content}</Link> : content}

      {item.menuItems && (
        <S.NodeContent collapsed={collapsed} hidden={!isOpen}>
          {collapsed && <S.TitleSubmenu>{i18n.t(item.titleKey)}</S.TitleSubmenu>}

          {item.menuItems.map((item, itemIndex) => {
            if (!hasPermission([item], verifyPermission)) return

            return (
              <MenuItemNode
                item={item}
                key={`${item.itemId}-${itemIndex}`}
                level={level + 1}
                onSubmenuToggle={onSubmenuToggle}
                openItems={openItems}
              />
            )
          })}
        </S.NodeContent>
      )}
    </S.ListItem>
  )
}
